/**
 * Gestational-age-*gated* lab/screening order sets (`BACKLOG.md` M11). Due windows are
 * **UNREVIEWED** — the general shape (NIPT from ~10 weeks, quad screen 14-22 weeks, GBS culture
 * 36-37 weeks specifically since culture validity is only ~5 weeks, GTT 24-28 weeks) is recalled
 * from general obstetric training-data knowledge, not independently re-verified against ACOG/USPSTF
 * primary sources this session — matching this module's `datingEngine.ts` and every prior specialty
 * module's own UNREVIEWED discipline. `resultKind` distinguishes screenings that report a binary/
 * categorical result from ones that report a risk score, per the source research's own note that
 * "several produce risk scores, not binary results" — `obgyn_screening_orders.result_summary` stays
 * free text specifically because a risk-score result (e.g. a NIPT aneuploidy risk) can't be
 * squeezed into a closed positive/negative vocabulary without losing information.
 */

export const OBGYN_SCREENING_IDS = ['nipt', 'quad_screen', 'gbs_culture', 'gtt'] as const;
export type ObgynScreeningId = (typeof OBGYN_SCREENING_IDS)[number];

export function isObgynScreeningId(value: string): value is ObgynScreeningId {
  return (OBGYN_SCREENING_IDS as readonly string[]).includes(value);
}

export interface ObgynScreeningDefinition {
  readonly id: ObgynScreeningId;
  readonly label: string;
  readonly windowStartDays: number;
  readonly windowEndDays: number;
  readonly resultKind: 'binary' | 'risk_score';
}

/** All windows in gestational-age days from LMP-equivalent (see `gestationalAge.ts`). UNREVIEWED. */
export const OBGYN_SCREENINGS: readonly ObgynScreeningDefinition[] = [
  {
    id: 'nipt',
    label: 'Cell-free DNA (NIPT)',
    windowStartDays: 70,
    windowEndDays: 280,
    resultKind: 'risk_score',
  },
  {
    id: 'quad_screen',
    label: 'Quad screen',
    windowStartDays: 98,
    windowEndDays: 154,
    resultKind: 'risk_score',
  },
  {
    id: 'gbs_culture',
    label: 'Group B Strep culture',
    // Culture validity is ~5 weeks, hence the narrow window rather than a plain "third trimester" cut.
    windowStartDays: 252,
    windowEndDays: 259,
    resultKind: 'binary',
  },
  {
    id: 'gtt',
    label: 'Glucose tolerance test',
    windowStartDays: 168,
    windowEndDays: 196,
    resultKind: 'binary',
  },
];

export function obgynScreeningDefinition(id: ObgynScreeningId): ObgynScreeningDefinition {
  const def = OBGYN_SCREENINGS.find((s) => s.id === id);
  if (def === undefined) throw new Error(`unknown obgyn screening id: ${id}`);
  return def;
}

export const SCREENING_DUE_STATUSES = ['not_yet_due', 'due', 'window_closing', 'overdue'] as const;
export type ScreeningDueStatus = (typeof SCREENING_DUE_STATUSES)[number];

/**
 * Due-window status purely off gestational age — has nothing to say about whether the screening was
 * actually ordered or completed (that's `screeningOrderService.ts`'s job, merging this against real
 * order rows). "window_closing" fires in the window's final 7 days, so a flowsheet can visually flag
 * a GBS-style narrow window before it's missed entirely, not just after.
 */
export function screeningDueStatus(
  definition: ObgynScreeningDefinition,
  gestationalAgeTotalDays: number,
): ScreeningDueStatus {
  if (gestationalAgeTotalDays < definition.windowStartDays) return 'not_yet_due';
  if (gestationalAgeTotalDays > definition.windowEndDays) return 'overdue';
  if (definition.windowEndDays - gestationalAgeTotalDays <= 7) return 'window_closing';
  return 'due';
}
